import type { Operation } from 'fast-json-patch';
import * as zod from 'zod';

import type {
  FirmwareIdentification,
  JointType,
  TemperatureReport,
} from '@sb/firmware-interface/types';

export enum ConnectedDeviceState {
  None = 'NONE',
  Initializing = 'INITIALIZING',
  Initialized = 'INITIALIZED',
  Disconnected = 'DISCONNECTED',
  Error = 'ERROR',
}

export interface SbBoard {
  id: string;
  boardSerialNumber: string;
  sbSerialNumber?: string;
  jointNumber: number;
  boardType: string;
}

export interface ConnectedDevice {
  jointNumber: number;
  jointType: JointType;
  state: ConnectedDeviceState;
  serialNumber: number;
  adapter: string;
  sbBoard?: SbBoard;
  lastMessageReceieved: number;
  errorMessage?: string;
  activeErrors: string[];
  warnings?: Record<string, string>;
  firmwareIdentification?: FirmwareIdentification;
  flashMetadata?: Record<string, string>;
}

export interface ConnectedArm {
  state: ConnectedDeviceState;
  serialNumber?: string;
  model?: string;
  joints: ConnectedDevice[];
}

export type SelectableDevice = ConnectedDevice | ConnectedArm;

export interface AssemblyJointState {
  jointNumber: number;
  position?: number;
  velocity?: number;
  validFeedback?: boolean;
  amperageTorque?: number;
  windupTorque?: number;
  braked?: boolean;
  mcuTemperature?: number;
  temperatureReport?: TemperatureReport;
  recentResults: TimedResult[];
}

export enum ResultLevel {
  Info = 'INFO',
  Warning = 'WARNING',
  Error = 'ERROR',
  Debug = 'DEBUG',
}

export interface TimedResult {
  timestamp: number;
  result: string;
  level: ResultLevel;
}

export enum RunnableMessageLevel {
  Info = 'INFO',
  Warning = 'WARNING',
  Error = 'ERROR',
  Debug = 'DEBUG',
}

export interface RunnableMessage {
  id: string;
  timestamp: number;
  level: RunnableMessageLevel;
  message: string;
  // Serial of the joint that produced this result. Stamped by robby-server so
  // the UI can show only the selected joint's results (results are broadcast on
  // one channel for all joints). 0 = unset (e.g. arm-level runs).
  serialNumber: number;
}

export interface RunnableData {
  runId: string;
  key: string;
  data: Record<string, number>;
  success: boolean;
  failureReason?: string;
}

export enum AssemblyResultStatus {
  Success = 'SUCCESS',
  Failure = 'FAILURE',
  Neutral = 'NEUTRAL',
  Incomplete = 'INCOMPLETE',
}

export interface AssemblyRunnableResult {
  key: string;
  runId: string;
  completed: boolean;
  resultStatus: AssemblyResultStatus;
  runData?: RunnableData;
  errors: RunnableMessage[];
}

export enum AssemblyExecutionState {
  NotStarted = 'NOT_STARTED',
  Running = 'RUNNING',
  Complete = 'COMPLETE',
}

export interface AssemblyRunnableState {
  runnableKey: string;
  id: string;
  startTime?: number;
  progress?: number;
  executionState: AssemblyExecutionState;
  statusText: string;
  result?: AssemblyRunnableResult;
  output: RunnableMessage[];
  uploadTask?: CloudUploadTask;
}

export enum UploadStatus {
  Pending = 'PENDING',
  Uploading = 'UPLOADING',
  Success = 'SUCCESS',
  Error = 'ERROR',
}

export interface CloudUploadTask {
  runId: string;
  status: UploadStatus;
  errorMessage?: string;
  /**
   * Per-leg progress. A run is only fully uploaded when both are true.
   * `dataUploaded` without `marvinUploaded` means the bytes are in GCS but no
   * Marvin row points at them. Undefined on servers that predate these fields.
   */
  marvinUploaded?: boolean;
  dataUploaded?: boolean;
  /** Unix-epoch ms the run was first queued — survives robby-server restarts. */
  createdAtMs?: number;
  /**
   * Run folder found on disk with no upload state to rehydrate from. Its data
   * can be pushed but the Marvin record can't be recreated automatically.
   */
  needsManualReview?: boolean;
}

export interface PeripheralDeviceState {
  id: string;
  type: string;
  connected: boolean;
  lastError: string;
  rxCount: number;
  lastLine?: string;
}

/**
 * Cached ST-LINK probe status mirrored from firmware-bot-cpp's watcher via
 * robby-server. The parent `stLink` field is `null` (not omitted) when
 * firmware-bot-cpp is unreachable, so the UI can distinguish "status unknown"
 * from "no target attached".
 */
export interface StLinkStatus {
  targetConnected: boolean;
  serialNumber?: number;
  /** Unix-epoch ms of the last successful probe — comparable to Date.now(). */
  lastSeenAtMs?: number;
}

export interface AssemblyServerState {
  desireLoopEnabled: boolean;
  connectedDevices?: { [key: string]: ConnectedDevice };
  jointStates?: { [key: string]: AssemblyJointState };
  connectedArm?: ConnectedArm;
  runnableStates?: { [key: string]: AssemblyRunnableState };
  cloudUploadTasks?: { [key: string]: CloudUploadTask };
  dcinVoltage?: number;
  dcinCurrent?: number;
  vbusVoltage?: number;
  /**
   * Whether `vbusVoltage` is a measurement (or a station-declared default)
   * rather than robby's startup initializer, which sits above the powered
   * threshold. Undefined on servers that predate the field — treat that as
   * reported, i.e. keep the old behaviour.
   */
  vbusReported?: boolean;
  runnables?: AssemblyRunnableInfo[];
  buildId?: string;
  buildName?: string;
  isStableBuild?: boolean;
  isEStopHeld?: boolean;
  isHardwareEStopActive?: boolean;
  /**
   * Software power command in flight, not yet confirmed by measured bus
   * voltage. `undefined` means the server predates verified power commands.
   * Current servers only track stops (power-ups are fire-and-forget);
   * `'start'` is still sent by older servers that tracked both directions.
   */
  powerCommandPending?: 'stop' | 'start' | null;
  /** The last power command exhausted its retries without the bus confirming. */
  powerCommandFailed?: boolean;
  /**
   * Server accepts the directional STOP_POWER/START_POWER commands. Absent on
   * older servers, which only understand the ESTOP toggle.
   */
  supportsDirectionalPower?: boolean;
  /**
   * Per-serial pipeline runs, keyed `sn{serial}` — mirroring `runnableStates`.
   * An entry is the latest run on that joint (RUNNING or terminal); it is
   * replaced when a new run starts on the serial and removed when the device
   * is removed.
   */
  pipelineRuns?: { [key: string]: PipelineRunState };
  /**
   * Most recent *terminal* run per serial per runnable key, keyed `sn{serial}`
   * then by runnable key. Unlike `runnableStates` (latest run on the serial,
   * replaced when any new runnable starts), an entry here survives until the
   * same runnable key completes again on the serial — so the pipeline UI can
   * show results/output for every step, not just the most recent one. Live
   * (RUNNING) state is never stored here.
   */
  latestRunsBySerial?: {
    [key: string]: { [runnableKey: string]: AssemblyRunnableState };
  };
  peripheralStates?: { [id: string]: PeripheralDeviceState };
  canInterfaces?: string[];
  /**
   * ST-LINK probe status. Always present in the server's full-state payload:
   * `null` means firmware-bot-cpp is unreachable (status unknown), while
   * `{ targetConnected: false }` means the probe ran and found no target.
   */
  stLink?: StLinkStatus | null;
  /**
   * Who streams desires on this station, from robby-server.json (written by
   * updater-bot on operation-mode reconcile). 'robby-server' = the operation
   * mode runs no arm-control-bot. Absent on older robby-servers and
   * unprovisioned stations — consumers must treat absence as "unknown", not
   * as either value.
   */
  desiresSource?: 'robby-server' | 'acb';
}

export interface GraphableMessage {
  timestamp: number;
  adapter: string;
  jointNumber: number;
  graphables: number[][];
}

export enum AssemblyInputParameterType {
  Float = 'FLOAT',
  Int = 'INT',
  Boolean = 'BOOLEAN',
  String = 'STRING',
  MultiString = 'MULTI_STRING',
}

export interface RunnableInputParameterDefaultValueByJointType {
  value: string;
  jointTypeValue?: Record<JointType | string, string>;
  variantValue?: Record<string, string>;
  // Key format: "JointType_MotorBoardVariant" e.g., "ShoulderJoint_THOR"
  jointTypeVariantValue?: Record<string, string>;
}

export interface RunnableInputParameterDescription {
  key: string;
  name: string;
  type: AssemblyInputParameterType;
  description: string;
  defaultValue?: RunnableInputParameterDefaultValueByJointType;
  units?: string;
  excludes: string[];
  required: boolean;
  alwaysShow: boolean;
  allowedValues?: string[];
  // Both set on a numeric param => renders a slider over [min, max] by step.
  minValue?: number;
  maxValue?: number;
  step?: number;
}

export enum AssemblyRunnableDeviceType {
  Joint = 'JOINT',
  Arm = 'ARM',
}

export interface AssemblyRunnableInfo {
  key: string;
  deviceType: AssemblyRunnableDeviceType;
  displayName: string;
  description: string;
  notes?: string;
  inputParameters: { [key: string]: RunnableInputParameterDescription };
  supportedVariants: string[];
  supportedFirmwareTypes: string[];
}

export type AssemblyParameterType = number | string | boolean;

export interface AssemblyRunnableParameter {
  key: string;
  value: AssemblyParameterType;
}

export interface AssemblyRunnableOptions {
  key: string;
  serialNumber: number;
  inputParameters: { [key: string]: AssemblyRunnableParameter };
  userGraphables: string[];
}

export const RunRequestResponse = zod.object({
  key: zod.string(),
  runId: zod.string(),
  success: zod.boolean(),
  errors: zod.array(zod.string()),
});

export type RunRequestResponse = zod.infer<typeof RunRequestResponse>;

export enum AssemblyMessageType {
  Update = 'UPDATE',
  Output = 'OUTPUT',
  Stop = 'STOP',
  /** Toggle — direction is decided by server state. Prefer the directional
   * commands below when `supportsDirectionalPower` is set. */
  EStop = 'ESTOP',
  StopPower = 'STOP_POWER',
  StartPower = 'START_POWER',
  PowerCycle = 'POWER_CYCLE',
  Remove = 'REMOVE',
  ResultOnJoint = 'RESULT_ON_JOINT',
  SetConfig = 'SET_CONFIG',
  Reboot = 'REBOOT',
  Graphable = 'GRAPHABLE',
  TriggerDataSync = 'TRIGGER_DATA_SYNC',
  RetryUpload = 'RETRY_UPLOAD',
  RequestFullState = 'REQUEST_FULL_STATE',
}

// Mapping of AssemblyMessageType to payload type
type AssemblyMessagePayloadType = {
  [AssemblyMessageType.Update]: Operation[];
  [AssemblyMessageType.Output]: RunnableMessage;
  [AssemblyMessageType.Stop]: { serialNumber: number } | null;
  [AssemblyMessageType.EStop]: null;
  [AssemblyMessageType.StopPower]: null;
  [AssemblyMessageType.StartPower]: null;
  [AssemblyMessageType.PowerCycle]: null;
  [AssemblyMessageType.Remove]: { serialNumber: number };
  [AssemblyMessageType.ResultOnJoint]: RunnableMessage;
  [AssemblyMessageType.SetConfig]: { [key: string]: any };
  [AssemblyMessageType.Reboot]: null;
  [AssemblyMessageType.Graphable]: GraphableMessage;
  [AssemblyMessageType.TriggerDataSync]: { serialNumber: number };
  [AssemblyMessageType.RetryUpload]: { runId: string };
  [AssemblyMessageType.RequestFullState]: null;
};

export interface AssemblyMessage<T extends AssemblyMessageType> {
  type: AssemblyMessageType;
  payload: AssemblyMessagePayloadType[T];
}

// ---------------------------------------------------------------------------
// Runnable pipelines
//
// These types mirror the C++ wire shapes emitted by robby-server over HTTP and
// the WebSocket state stream (see cpp/libs/assembly-lib/include/assembly-lib/
// serialization/Serialization.h). Optional fields are omitted from the JSON
// payload when absent on the server — never serialized as `null` — so the TS
// types use `?:` rather than `T | null`.
// ---------------------------------------------------------------------------

// Derived from `AssemblyRunnableDeviceType` so the wire-shape stays a string
// literal union (matching the JSON the server emits) while keeping the two
// representations in lock-step — extending the enum automatically extends
// this union, no manual mirroring.
export type DeviceType = `${AssemblyRunnableDeviceType}`;

export interface PipelineStep {
  runnableKey: string;
  inputParameters: Record<string, unknown>;
}

export interface PipelineDefinition {
  key: string;
  displayName: string;
  deviceType: DeviceType;
  steps: PipelineStep[];
  /**
   * Hidden pipelines are omitted from pipeline pickers but stay in the
   * catalog (so an in-flight run still renders its display name and step
   * labels) and remain startable by key. The server only emits the field
   * when true. This is a UI-listing concern, NOT an access-control flag:
   * the server accepts a run request for any pipeline key regardless.
   */
  hidden?: boolean;
}

// Runtime validators for the pipeline payloads that cross the robby-server
// (C++) boundary. `fetchPipelines` parses against these so a wire-format drift
// fails loudly instead of flowing through as mistyped data — matching the
// `RunRequestResponse.parse` convention above.
export const PipelineStepSchema = zod.object({
  runnableKey: zod.string(),
  inputParameters: zod.record(zod.unknown()),
});

export const PipelineDefinitionSchema = zod.object({
  key: zod.string(),
  displayName: zod.string(),
  deviceType: zod.nativeEnum(AssemblyRunnableDeviceType),
  steps: zod.array(PipelineStepSchema),
  hidden: zod.boolean().optional(),
});

// GET /pipelines/ returns `{ pipelines: PipelineDefinition[] }`. Default the
// array so an omitted key parses to an empty catalog rather than throwing.
export const PipelineListResponseSchema = zod.object({
  pipelines: zod.array(PipelineDefinitionSchema).default([]),
});

export type PipelineRunStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'STOPPED_BY_USER';

export type PipelineStepStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'SKIPPED'
  | 'CANCELED'
  | 'STOPPED_BY_USER';

export interface PipelineStepResult {
  stepIndex: number;
  runnableKey: string;
  runId?: string;
  status: PipelineStepStatus;
  startedAt?: string;
  completedAt?: string;
}

/**
 * Per-run parameter overrides, keyed by runnable key → parameter key →
 * value. Merged server-side over each matching step's static
 * `inputParameters` and persisted on the run, so resumes relaunch steps
 * with the same values. Fresh starts only — the server rejects overrides
 * on a resume request.
 */
export interface PipelineParameterOverrides {
  [runnableKey: string]: {
    [parameterKey: string]: string | number | boolean;
  };
}

export interface PipelineRunState {
  pipelineRunId: string;
  pipelineKey: string;
  boardSerialNumber: string;
  status: PipelineRunStatus;
  startedAt: string;
  completedAt?: string;
  stepResults: PipelineStepResult[];
  /**
   * Per-run parameter overrides captured when the run was started. Omitted
   * by the server when the run has none.
   */
  parameterOverrides?: PipelineParameterOverrides;
}
